import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import apiClient, { getErrorMessage } from '../../api/client';
import { useToast } from '../../hooks/useToast';
import { FiPlus } from 'react-icons/fi';

const TABS = ['PAYE Schemes', 'Pension Schemes', 'Employee Settings', 'HMRC Submissions'];

const PayrollSettings = () => {
  const { success, error: showError } = useToast();
  const [tab, setTab] = useState('PAYE Schemes');
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const [payeSchemes, setPayeSchemes] = useState([]);
  const [pensionSchemes, setPensionSchemes] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [submissions, setSubmissions] = useState([]);

  const [payeForm, setPayeForm] = useState({ payeReference: '', accountsOfficeReference: '', taxYearStart: '' });
  const [pensionForm, setPensionForm] = useState({ providerName: '', schemeReference: '', schemeType: 'auto_enrolment' });
  const [submissionForm, setSubmissionForm] = useState({ submissionType: 'FPS', payeSchemeId: '', periodReference: '' });

  const [selectedEmployeeId, setSelectedEmployeeId] = useState('');
  const [taxDetails, setTaxDetails] = useState(null);
  const [pensionMembership, setPensionMembership] = useState(null);
  const [taxForm, setTaxForm] = useState({ taxCode: '', niCategory: '', studentLoanPlan: 'none', effectiveFrom: new Date().toISOString().slice(0, 10), source: 'starter_checklist' });
  const [pensionMemberForm, setPensionMemberForm] = useState({ pensionSchemeId: '', employeeContributionPct: '5', employerContributionPct: '3', enrolmentDate: new Date().toISOString().slice(0, 10), optedOut: false });

  const loadAll = () => {
    setLoading(true);
    Promise.all([
      apiClient.get('/payroll/schemes/paye'),
      apiClient.get('/payroll/schemes/pension'),
      apiClient.get('/hr/employees'),
      apiClient.get('/payroll/hmrc-submissions'),
    ]).then(([payeRes, pensionRes, empRes, subRes]) => {
      setPayeSchemes(payeRes.data.data);
      setPensionSchemes(pensionRes.data.data);
      setEmployees(empRes.data.data);
      setSubmissions(subRes.data.data);
    }).catch((err) => showError(getErrorMessage(err, 'Could not load payroll settings')))
      .finally(() => setLoading(false));
  };

  useEffect(loadAll, []); // eslint-disable-line react-hooks/exhaustive-deps

  const employeeName = (id) => {
    const e = employees.find((x) => x.id === id);
    return e ? `${e.firstName} ${e.lastName}` : id;
  };
  const payeRefLabel = (id) => payeSchemes.find((p) => p.id === id)?.payeReference || id;

  // ---- PAYE Schemes ----
  const handleAddPaye = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post('/payroll/schemes/paye', payeForm);
      success('✅ PAYE scheme added');
      setShowForm(false);
      setPayeForm({ payeReference: '', accountsOfficeReference: '', taxYearStart: '' });
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not add PAYE scheme')); }
  };

  // ---- Pension Schemes ----
  const handleAddPension = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post('/payroll/schemes/pension', pensionForm);
      success('✅ Pension scheme added');
      setShowForm(false);
      setPensionForm({ providerName: '', schemeReference: '', schemeType: 'auto_enrolment' });
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not add pension scheme')); }
  };

  // ---- Employee Settings ----
  const loadEmployeeSettings = async (employeeId) => {
    setSelectedEmployeeId(employeeId);
    setTaxDetails(null);
    setPensionMembership(null);
    if (!employeeId) return;
    try {
      const [taxRes, pensionRes] = await Promise.all([
        apiClient.get(`/payroll/employees/${employeeId}/tax-details`).catch(() => ({ data: { data: null } })),
        apiClient.get(`/payroll/employees/${employeeId}/pension-membership`).catch(() => ({ data: { data: null } })),
      ]);
      setTaxDetails(taxRes.data.data);
      setPensionMembership(pensionRes.data.data);
    } catch (err) { showError(getErrorMessage(err, 'Could not load employee settings')); }
  };

  const handleSetTaxDetails = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...taxForm };
      if (!payload.niCategory) delete payload.niCategory;
      await apiClient.post(`/payroll/employees/${selectedEmployeeId}/tax-details`, payload);
      success('✅ Tax details updated');
      loadEmployeeSettings(selectedEmployeeId);
    } catch (err) { showError(getErrorMessage(err, 'Could not set tax details')); }
  };

  const handleSetPensionMembership = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...pensionMemberForm };
      if (!payload.optedOut) delete payload.optOutDate;
      await apiClient.post(`/payroll/employees/${selectedEmployeeId}/pension-membership`, payload);
      success('✅ Pension membership updated');
      loadEmployeeSettings(selectedEmployeeId);
    } catch (err) { showError(getErrorMessage(err, 'Could not set pension membership')); }
  };

  // ---- HMRC Submissions ----
  const handleCreateSubmission = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...submissionForm };
      if (!payload.periodReference) delete payload.periodReference;
      await apiClient.post('/payroll/hmrc-submissions', payload);
      success('✅ Submission logged');
      setShowForm(false);
      setSubmissionForm({ submissionType: 'FPS', payeSchemeId: '', periodReference: '' });
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not log submission')); }
  };

  return (
    <motion.div className="dashboard-page" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
      <div className="page-header">
        <h1>Payroll Settings</h1>
        <p>PAYE & pension schemes, employee tax/pension details, and HMRC submissions</p>
      </div>

      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', borderBottom: '1px solid #e5e7eb', flexWrap: 'wrap' }}>
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => { setTab(t); setShowForm(false); }}
            style={{
              padding: '0.75rem 1rem', border: 'none', background: 'none', cursor: 'pointer',
              fontWeight: 600, color: tab === t ? '#3b82f6' : '#6b7280',
              borderBottom: tab === t ? '2px solid #3b82f6' : '2px solid transparent',
            }}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === 'PAYE Schemes' && (
        <section className="page-section">
          <div className="section-header">
            <h2>PAYE Schemes</h2>
            <button className="btn btn-sm btn-primary" style={{ width: 'auto' }} onClick={() => setShowForm((s) => !s)}>
              <FiPlus size={14} /> {showForm ? 'Cancel' : 'Add Scheme'}
            </button>
          </div>
          {showForm && (
            <form onSubmit={handleAddPaye} className="form-grid" style={{ marginBottom: '1.5rem' }}>
              <div className="form-field"><label>PAYE Reference *</label><input required value={payeForm.payeReference} onChange={(e) => setPayeForm(p => ({ ...p, payeReference: e.target.value }))} placeholder="123/AB45678" /></div>
              <div className="form-field"><label>Accounts Office Reference *</label><input required value={payeForm.accountsOfficeReference} onChange={(e) => setPayeForm(p => ({ ...p, accountsOfficeReference: e.target.value }))} /></div>
              <div className="form-field"><label>Tax Year Start *</label><input required type="date" value={payeForm.taxYearStart} onChange={(e) => setPayeForm(p => ({ ...p, taxYearStart: e.target.value }))} /></div>
              <div className="form-field full-width form-actions"><button type="submit" className="btn btn-primary" style={{ width: 'auto' }}>Add</button></div>
            </form>
          )}
          <div className="table-container">
            <table className="data-table">
              <thead><tr><th>PAYE Ref</th><th>Accounts Office Ref</th><th>Active</th></tr></thead>
              <tbody>
                {payeSchemes.length === 0 ? (
                  <tr><td colSpan={3} style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>No PAYE schemes yet</td></tr>
                ) : payeSchemes.map((p) => (
                  <tr key={p.id}>
                    <td style={{ fontWeight: 600 }}>{p.payeReference}</td>
                    <td>{p.accountsOfficeReference}</td>
                    <td><span className="badge" style={{ background: p.isActive ? '#10b981' : '#9ca3af', color: 'white' }}>{p.isActive ? 'Yes' : 'No'}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {tab === 'Pension Schemes' && (
        <section className="page-section">
          <div className="section-header">
            <h2>Pension Schemes</h2>
            <button className="btn btn-sm btn-primary" style={{ width: 'auto' }} onClick={() => setShowForm((s) => !s)}>
              <FiPlus size={14} /> {showForm ? 'Cancel' : 'Add Scheme'}
            </button>
          </div>
          {showForm && (
            <form onSubmit={handleAddPension} className="form-grid" style={{ marginBottom: '1.5rem' }}>
              <div className="form-field"><label>Provider Name *</label><input required value={pensionForm.providerName} onChange={(e) => setPensionForm(p => ({ ...p, providerName: e.target.value }))} placeholder="NEST" /></div>
              <div className="form-field"><label>Scheme Reference</label><input value={pensionForm.schemeReference} onChange={(e) => setPensionForm(p => ({ ...p, schemeReference: e.target.value }))} /></div>
              <div className="form-field">
                <label>Type</label>
                <select value={pensionForm.schemeType} onChange={(e) => setPensionForm(p => ({ ...p, schemeType: e.target.value }))}>
                  <option value="auto_enrolment">Auto Enrolment</option>
                  <option value="salary_sacrifice">Salary Sacrifice</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="form-field full-width form-actions"><button type="submit" className="btn btn-primary" style={{ width: 'auto' }}>Add</button></div>
            </form>
          )}
          <div className="table-container">
            <table className="data-table">
              <thead><tr><th>Provider</th><th>Reference</th><th>Type</th><th>Active</th></tr></thead>
              <tbody>
                {pensionSchemes.length === 0 ? (
                  <tr><td colSpan={4} style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>No pension schemes yet</td></tr>
                ) : pensionSchemes.map((p) => (
                  <tr key={p.id}>
                    <td style={{ fontWeight: 600 }}>{p.providerName}</td>
                    <td>{p.schemeReference || '—'}</td>
                    <td style={{ textTransform: 'capitalize' }}>{p.schemeType?.replace('_', ' ')}</td>
                    <td><span className="badge" style={{ background: p.isActive ? '#10b981' : '#9ca3af', color: 'white' }}>{p.isActive ? 'Yes' : 'No'}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {tab === 'Employee Settings' && (
        <section className="page-section">
          <div className="section-header"><h2>Employee Tax & Pension Details</h2></div>
          <div className="form-field" style={{ maxWidth: '400px', marginBottom: '1.5rem' }}>
            <label>Employee</label>
            <select value={selectedEmployeeId} onChange={(e) => loadEmployeeSettings(e.target.value)}>
              <option value="">-- select an employee --</option>
              {employees.map((e) => <option key={e.id} value={e.id}>{e.firstName} {e.lastName}</option>)}
            </select>
          </div>

          {selectedEmployeeId && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
              <div>
                <h3 style={{ fontSize: '1rem', marginBottom: '0.75rem' }}>Tax Details</h3>
                {taxDetails && (
                  <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '6px', padding: '0.75rem', marginBottom: '1rem', fontSize: '0.85rem' }}>
                    Current: <strong>{taxDetails.taxCode}</strong> · NI: {taxDetails.niCategory || '—'} · Student loan: {taxDetails.studentLoanPlan || 'none'} · since {new Date(taxDetails.effectiveFrom).toLocaleDateString()}
                  </div>
                )}
                <form onSubmit={handleSetTaxDetails} className="form-grid">
                  <div className="form-field"><label>Tax Code *</label><input required value={taxForm.taxCode} onChange={(e) => setTaxForm(p => ({ ...p, taxCode: e.target.value }))} placeholder="1257L" /></div>
                  <div className="form-field"><label>NI Category</label><input value={taxForm.niCategory} onChange={(e) => setTaxForm(p => ({ ...p, niCategory: e.target.value }))} placeholder="A" /></div>
                  <div className="form-field">
                    <label>Student Loan</label>
                    <select value={taxForm.studentLoanPlan} onChange={(e) => setTaxForm(p => ({ ...p, studentLoanPlan: e.target.value }))}>
                      <option value="none">None</option>
                      <option value="plan1">Plan 1</option>
                      <option value="plan2">Plan 2</option>
                      <option value="plan4">Plan 4</option>
                      <option value="postgrad">Postgrad</option>
                    </select>
                  </div>
                  <div className="form-field"><label>Effective From *</label><input required type="date" value={taxForm.effectiveFrom} onChange={(e) => setTaxForm(p => ({ ...p, effectiveFrom: e.target.value }))} /></div>
                  <div className="form-field">
                    <label>Source</label>
                    <select value={taxForm.source} onChange={(e) => setTaxForm(p => ({ ...p, source: e.target.value }))}>
                      <option value="starter_checklist">Starter Checklist</option>
                      <option value="p45">P45</option>
                      <option value="p46">P46</option>
                      <option value="hmrc_notice">HMRC Notice</option>
                    </select>
                  </div>
                  <div className="form-field full-width form-actions"><button type="submit" className="btn btn-primary" style={{ width: 'auto' }}>Save Tax Details</button></div>
                </form>
              </div>

              <div>
                <h3 style={{ fontSize: '1rem', marginBottom: '0.75rem' }}>Pension Membership</h3>
                {pensionMembership && (
                  <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '6px', padding: '0.75rem', marginBottom: '1rem', fontSize: '0.85rem' }}>
                    Employee {pensionMembership.employeeContributionPct}% · Employer {pensionMembership.employerContributionPct}% {pensionMembership.optedOut ? '· Opted out' : ''}
                  </div>
                )}
                <form onSubmit={handleSetPensionMembership} className="form-grid">
                  <div className="form-field">
                    <label>Scheme *</label>
                    <select required value={pensionMemberForm.pensionSchemeId} onChange={(e) => setPensionMemberForm(p => ({ ...p, pensionSchemeId: e.target.value }))}>
                      <option value="">-- select --</option>
                      {pensionSchemes.map((s) => <option key={s.id} value={s.id}>{s.providerName}</option>)}
                    </select>
                  </div>
                  <div className="form-field"><label>Employee Contribution %</label><input type="number" step="0.1" value={pensionMemberForm.employeeContributionPct} onChange={(e) => setPensionMemberForm(p => ({ ...p, employeeContributionPct: e.target.value }))} /></div>
                  <div className="form-field"><label>Employer Contribution %</label><input type="number" step="0.1" value={pensionMemberForm.employerContributionPct} onChange={(e) => setPensionMemberForm(p => ({ ...p, employerContributionPct: e.target.value }))} /></div>
                  <div className="form-field"><label>Enrolment Date *</label><input required type="date" value={pensionMemberForm.enrolmentDate} onChange={(e) => setPensionMemberForm(p => ({ ...p, enrolmentDate: e.target.value }))} /></div>
                  <div className="form-field">
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', textTransform: 'none' }}>
                      <input type="checkbox" checked={pensionMemberForm.optedOut} onChange={(e) => setPensionMemberForm(p => ({ ...p, optedOut: e.target.checked }))} style={{ width: 'auto' }} />
                      Opted out
                    </label>
                  </div>
                  <div className="form-field full-width form-actions"><button type="submit" className="btn btn-primary" style={{ width: 'auto' }}>Save Pension Membership</button></div>
                </form>
              </div>
            </div>
          )}
        </section>
      )}

      {tab === 'HMRC Submissions' && (
        <section className="page-section">
          <div className="section-header">
            <h2>HMRC Submissions</h2>
            <button className="btn btn-sm btn-primary" style={{ width: 'auto' }} onClick={() => setShowForm((s) => !s)}>
              <FiPlus size={14} /> {showForm ? 'Cancel' : 'Log Submission'}
            </button>
          </div>
          <p style={{ color: '#6b7280', fontSize: '0.85rem', marginBottom: '1rem' }}>
            ⚠️ This environment has no live HMRC Government Gateway connection — submissions are logged here for record-keeping, but genuinely sit as "pending" forever since nothing actually transmits them or receives a real acknowledgement.
          </p>
          {showForm && (
            <form onSubmit={handleCreateSubmission} className="form-grid" style={{ marginBottom: '1.5rem' }}>
              <div className="form-field">
                <label>Type *</label>
                <select value={submissionForm.submissionType} onChange={(e) => setSubmissionForm(p => ({ ...p, submissionType: e.target.value }))}>
                  <option value="FPS">FPS (Full Payment Submission)</option>
                  <option value="EPS">EPS (Employer Payment Summary)</option>
                </select>
              </div>
              <div className="form-field">
                <label>PAYE Scheme *</label>
                <select required value={submissionForm.payeSchemeId} onChange={(e) => setSubmissionForm(p => ({ ...p, payeSchemeId: e.target.value }))}>
                  <option value="">-- select --</option>
                  {payeSchemes.map((s) => <option key={s.id} value={s.id}>{s.payeReference}</option>)}
                </select>
              </div>
              <div className="form-field"><label>Period Reference</label><input value={submissionForm.periodReference} onChange={(e) => setSubmissionForm(p => ({ ...p, periodReference: e.target.value }))} placeholder="Month 6" /></div>
              <div className="form-field full-width form-actions"><button type="submit" className="btn btn-primary" style={{ width: 'auto' }}>Log Submission</button></div>
            </form>
          )}
          <div className="table-container">
            <table className="data-table">
              <thead><tr><th>Type</th><th>PAYE Scheme</th><th>Period</th><th>Status</th><th>Date</th></tr></thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={5} style={{ textAlign: 'center', padding: '2rem' }}>Loading...</td></tr>
                ) : submissions.length === 0 ? (
                  <tr><td colSpan={5} style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>No submissions logged yet</td></tr>
                ) : submissions.map((s) => (
                  <tr key={s.id}>
                    <td style={{ fontWeight: 600 }}>{s.submissionType}</td>
                    <td>{payeRefLabel(s.payeSchemeId)}</td>
                    <td>{s.periodReference || '—'}</td>
                    <td><span className="badge" style={{ background: '#f59e0b', color: 'white' }}>{s.status}</span></td>
                    <td>{new Date(s.createdAt).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </motion.div>
  );
};

export default PayrollSettings;